"use client";

import {MouseEventHandler, useCallback, useMemo} from "react";
import HamburgerMenuButton from "./HamburgerMenuButton";
import Drawer, {AnchorSide} from "../../Drawer";
import useRouterEffect from "../../useRouterEffect";
import useModalAnimation from "../../useModalAnimation";
import MobileMenu from "./MobileMenu";
import {
	useCampusNavItem,
	UseCampusNavItemConfig,
} from "@/app/util/useCampusNavItem";
import {TOP_LEVEL_MENU_ITEMS} from "@/app/util/navigationMenuConstants";
import staticUrls from "@/app/util/staticPageUrls";
import {useUserInfo} from "../../contexts/userInfoContext";
import {MenuItem} from "./MenuTypes";
import {MobileMenuItemContextProvider} from "./MobileMenuItemContext";
import {FCWithClassName} from "@/app/types/reactTypes";
import {useCampusContext} from "@/app/_components/contexts/CampusCookieContext";
import CampusLabel from "../CampusLabel";
import LoadingLocationLabel from "../LoadingLocationLabel";
import useDynamicMenuItems from "../useDynamicMenuItems";

const HamburgerMenu: FCWithClassName = ({className}) => {
	/**
	 * Track animation state instead of using AnimatePresence per RAC recommendation:
	 * https://react-spectrum.adobe.com/react-aria/styling.html
	 * */
	const [open, animation, handlers] = useModalAnimation();

	useRouterEffect({onChange: handlers.closeModal});
	const {userInfo} = useUserInfo();
	const {campus} = useCampusContext();
	const campusNavItemConfig = useMemo<UseCampusNavItemConfig>(() => {
		if (!campus) {
			return {};
		}
		return {
			campusLabelComponent: <CampusLabel campusName={campus.locationName} />,
			loadingComponent: <LoadingLocationLabel />,
		};
	}, [campus]);
	const {campusNavItemProps} = useCampusNavItem(campusNavItemConfig);

	const dynamicMenuItems = useDynamicMenuItems(TOP_LEVEL_MENU_ITEMS);
	const menuItems: MenuItem[] = useMemo(() => {
		const mobileMenuItems: MenuItem[] = [
			{
				label: "Get Started",
				id: "contact",
				href: staticUrls.contact,
			},
			{
				label: "Cart",
				id: "cart",
				href: staticUrls.cart,
				requireLogin: true,
			},
		];

		// add campus nav element if it exists (the user has a campus selected)
		if (campusNavItemProps) {
			mobileMenuItems.unshift(campusNavItemProps);
		}

		return [...dynamicMenuItems, ...mobileMenuItems];
	}, [campusNavItemProps, dynamicMenuItems]);

	const onNavigateToCurrentPage: MouseEventHandler<HTMLAnchorElement> =
		useCallback(() => {
			handlers.closeModal();
			window.scrollTo(0, 0);
		}, [handlers]);

	return (
		<MobileMenuItemContextProvider
			onNavigateToCurrentPage={onNavigateToCurrentPage}
		>
			<HamburgerMenuButton className={className} onPress={handlers.openModal} />

			<Drawer
				animationState={animation}
				setAnimation={handlers.setAnimationState}
				anchorSide={AnchorSide.RIGHT}
				isOpen={open}
				onOpenChange={handlers.setIsOpen}
				showCloseButton
				classes={{
					closeButton:
						"bg-green-900 text-white duration-300 hover:bg-green-800",
				}}
			>
				<MobileMenu
					userInfo={userInfo}
					campusData={campus}
					menuItems={menuItems}
				/>
			</Drawer>
		</MobileMenuItemContextProvider>
	);
};

export default HamburgerMenu;
